import { useTranslations } from 'next-intl';
import React, { Fragment } from 'react';
import useOfferComparisonStore, {
  priceSelector,
  selectPromoValue,
  selectTaxRate,
  selectTotalAnnualCostsInclPromo,
  selectTotalEnergyCostsInclPromo,
} from '../store/use-offer-comparison-store';
import { EuroSignPosition, formatPrice } from '../utils/format-price';
import useEnergyCalculatorStore from '../store/use-energy-calculator-store';
import { CustomerType } from '@/lib/offer-service/types/customer-type';
import PriceBreakdownRow from './price-breakdown-row';
import PriceBreakdownLayout from './price-breakdown-layout';
import BreakdownHeader, { PricePerKwhData } from './price-breakdown-header';

export default function PriceBreakdownAustria() {
  const t = useTranslations('priceBreakdown');
  const headingStyles = 'leading-8 font-bold text-lg pt-6 pb-4 ';
  const offer = useOfferComparisonStore((state) => state.offer);
  const taxRate = useOfferComparisonStore(selectTaxRate);
  const netCosts = offer?.netCosts;
  const promo = offer?.promoDiscount;
  const customerType = useEnergyCalculatorStore((state) => state.customerType);
  const isPrivateCustomer = customerType === CustomerType.Home;
  const promoValue = useOfferComparisonStore(selectPromoValue);
  const totalEnergyCostsInclPromo = useOfferComparisonStore(
    selectTotalEnergyCostsInclPromo
  );
  const totalAnnualCostsInclPromo = useOfferComparisonStore(
    selectTotalAnnualCostsInclPromo
  );
  const totalEnergyCosts = netCosts?.totalEnergyCosts ?? 0;
  const offerState = useOfferComparisonStore();
  const adjustPrice = (price: number) => priceSelector(offerState, price);
  const annualCostsExclDiscounts = adjustPrice(
    netCosts?.totalAnnualCostExclDiscounts ?? 0
  );

  const kwhPriceInCents =
    priceSelector(offerState, netCosts?.pricePerkWh ?? 0) * 100;

  const pricePerKwh: PricePerKwhData = {
    label: isPrivateCustomer
      ? t('proKwhWithTaxesTitle')
      : t('proKwhWithoutTaxesTitle'),
    value: `${formatPrice(kwhPriceInCents, true, EuroSignPosition.None)} ${t('cents')}`,
    labelTestId: isPrivateCustomer
      ? 'energyCalculatorIndividualOfferPricePerKwhPrivate'
      : 'energyCalculatorIndividualOfferPricePerKwhBusiness',
    valueTestId: isPrivateCustomer
      ? 'energyCalculatorIndividualOfferPriceCents'
      : 'energyCalculatorOfferPricePerKwhBusinessValue',
  };

  const sections = [
    {
      heading: t('energyCosts'),
      rows: [
        {
          title: t('energyWorkingPrice'),
          value: netCosts?.energyWorkingPrice ?? 0,
        },
        {
          title: t('energyBasePrice'),
          value: netCosts?.energyBasePrice ?? 0,
        },
        { title: t('energyFees'), value: netCosts?.energyFees ?? 0 },
        {
          title: t('totalEnergyCosts'),
          value: totalEnergyCosts,
          isHighlighted: true,
        },
        {
          title: t('taxes'),
          value: totalEnergyCosts * taxRate,
          isVisible: isPrivateCustomer,
        },
        {
          title: t('energyCostsInclTaxes'),
          value: adjustPrice(totalEnergyCosts),
          isHighlighted: true,
          isVisible: isPrivateCustomer,
        },
        {
          title: t('energyBonus'),
          value: -adjustPrice(netCosts?.energyDiscounts ?? 0),
          footnoteNumber: 1,
        },
        ...(promo
          ? [
              {
                title: promo.campaignName,
                value: -promoValue,
                testIdTitle: 'offerBreakdownCampaignTitle',
                testIdValue: 'offerBreakdownCampaignValue',
              },
            ]
          : []),
        {
          title: t('energyFeeReduction'),
          value: -adjustPrice(netCosts?.energyFeesReduction ?? 0),
        },
        {
          title: t('energyCostsInclDiscounts'),
          value:
            totalEnergyCostsInclPromo == null ||
            totalEnergyCostsInclPromo === adjustPrice(totalEnergyCosts)
              ? 0
              : totalEnergyCostsInclPromo,
          isHighlighted: true,
        },
      ],
    },
    {
      heading: t('gridCostsTitle'),
      rows: [
        {
          title: isPrivateCustomer
            ? t('gridCostsInclTaxes')
            : t('gridCostsExclTaxes'),
          value: adjustPrice(netCosts?.totalGridCosts ?? 0),
          isHighlighted: true,
        },
      ],
    },
    {
      heading: t('total'),
      rows: [
        {
          title: isPrivateCustomer
            ? t('totalCostsInclTaxes')
            : t('totalCostsExclTaxes'),
          value: totalAnnualCostsInclPromo ?? 0,
          isHighlighted: true,
          footnoteNumber:
            netCosts?.energyDiscounts == null || netCosts.energyDiscounts === 0
              ? 1
              : 2,
          testIdTitle: 'energyCalculatorIndividualOffer1YCostsPrivate',
          testIdValue: 'energyCalculatorIndividualOfferTotal1YCosts',
        },
        {
          title: t('totalCostsExclDiscounts'),
          value:
            annualCostsExclDiscounts === totalAnnualCostsInclPromo
              ? 0
              : annualCostsExclDiscounts,
          testIdTitle: 'offerBreakdownAnnualTotalTitle',
          testIdValue: 'offerBreakdownAnnualTotalValue',
        },
      ],
    },
  ];

  return (
    <PriceBreakdownLayout
      header={<BreakdownHeader pricePerKwh={pricePerKwh} />}
    >
      <div>
        {sections.map((section) => (
          <Fragment key={section.heading}>
            <p
              data-testid="electricity-costs-heading"
              className={headingStyles}
            >
              {section.heading}
            </p>
            {section.rows.map((row) => (
              <PriceBreakdownRow key={row.title} {...row} />
            ))}
          </Fragment>
        ))}
      </div>
    </PriceBreakdownLayout>
  );
}
